// StepShape — step 7, the final step. The user picks the project "shape"
// (Web App, Data Pipeline, ML/AI, …). The shape selects the generation
// RECIPE: which agents run in Phase 3, which rules the Critic applies, and
// the default output template. The panel below the grid previews the recipe
// name and agent roster for the current pick.
//
// This is the last step, so pressing "Generate Requirements" here fires the
// submit flow in NewProjectWizardPage.tsx (create project → upload files →
// intake → briefing → navigate). A shape must be selected to submit.
import type { ComponentType } from 'react'
import * as LucideIcons from 'lucide-react'
import { Box, Shapes } from 'lucide-react'

import { PROJECT_SHAPES, getShape } from '@/components/intake/shapes'
import type { Detected, NewProjectForm, UpdateForm } from '../types'

function iconFor(name: string): ComponentType<{ size?: number; className?: string }> {
  const pascal = name
    .split(/[-_\s]/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('')
  const lib = LucideIcons as unknown as Record<string, ComponentType<{ size?: number; className?: string }>>
  return lib[pascal] ?? Box
}

export function StepShape({
  form,
  update,
  detected,
}: {
  form: NewProjectForm
  update: UpdateForm
  detected: Detected
}) {
  const selected = form.shape ? getShape(form.shape) : null
  const confidencePct = Math.round(detected.shape.score * 100)

  return (
    <>
      <header className="step-header">
        <div className="step-section-tag">
          <Shapes size={14} />
          Project Shape
        </div>
        <h1 className="step-h1">What kind of <em>solution</em> are we building?</h1>
        <p className="step-lede">
          This picks the <span className="accent">workflow recipe</span> — which agents run, which
          rules the Critic applies, which template the system defaults to. You can change it later,
          but earlier is cheaper.
        </p>
      </header>

      <div className="archetype-grid">
        {PROJECT_SHAPES.map((s) => {
          const Icon = iconFor(s.icon)
          return (
            <button
              key={s.id}
              type="button"
              className={`archetype-card${form.shape === s.id ? ' selected' : ''}`}
              onClick={() => update({ shape: s.id })}
            >
              <div className="archetype-card-head">
                <Icon size={26} className="archetype-icon" />
                <div className="archetype-check">✓</div>
              </div>
              <div className="archetype-title">{s.label}</div>
              <div className="archetype-desc">{s.hint}</div>
            </button>
          )
        })}
      </div>

      {selected && (
        <section className="recipe-panel">
          <div className="recipe-head">
            <span className="recipe-tag">Routed to recipe:</span>
            <span className="recipe-value">{selected.recipe}</span>
          </div>
          <div className="recipe-agents">
            {selected.agents.map((a) => (
              <span key={a} className="recipe-agent">{a}</span>
            ))}
          </div>
          <div className="recipe-note">
            {selected.agents.length} agents will run in Phase 3 · swarm width adapts to recipe
          </div>
        </section>
      )}

      {detected.shape.shape && (
        <section className="detected-panel">
          <div className="detected-eyebrow">Detected from your description</div>
          <div className="detected-row">
            <span className="detected-row-label">Shape <span className="arrow">→</span></span>
            <span className="detected-key">{detected.shape.shape.label}</span>
            <span className="detected-confidence">
              <b>{confidencePct}% confidence</b> · you can change it here
            </span>
          </div>
        </section>
      )}
    </>
  )
}
